import React, { useEffect, useRef, useState } from "react";
import Dropzone from "react-dropzone";
import find from "lodash/find";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { acceptedFileTypes } from "../../utils/constants";
import DocumentListRefactor from "./DocumentListRefactorBeta";
import DocumentListViewOnly from "../DocumentListViewOnly";
import "./DocumentDropComponentBeta.css";

const DocumentDropContainer = styled("div")({
    padding: 0,
    marginBottom: "20px",
});

const DropzoneStyled = styled("div")({
    borderRadius: "3px",
    border: "2px dashed #dbdbdb",
});

const IconSmall = styled(CloudUploadIcon)({
    width: "40px",
    height: "40px",
    color: "#9E9E9E",
});

const DropzoneSmallTextWrapper = styled("div")({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "20px",
    justifyContent: "center",
});

const standardHeaders = [
    {
        name: "Name",
        prop: "documentName",
        type: "plaintext",
        extraHeaderClassName: "standardHeaderCol1",
    },
    {
        name: "Document Type",
        prop: "documentType",
        type: "plaintext",
        extraHeaderClassName: "standardHeaderCol2",
    },
    {
        name: "",
        prop: "",
        type: "plaintext",
        extraHeaderClassName: "standardHeaderCol3",
    },
];

const approvalHeaders = [
    {
        name: "",
        prop: "",
        type: "plaintext",
        extraHeaderClassName: "approvalHeaderCol1",
    },
    {
        name: "Name",
        prop: "documentName",
        type: "plaintext",
        extraHeaderClassName: "approvalHeaderCol2",
    },
    {
        name: "Document Type",
        prop: "documentType",
        type: "plaintext",
        extraHeaderClassName: "approvalHeaderCol3",
    },
    {
        name: "Approved By",
        prop: "",
        type: "plaintext",
        extraHeaderClassName: "approvalHeaderCol4",
    },
];

const DocumentDrop = (props) => {
    const {
        allDocumentTypes,
        addDocuments,
        approveDocument,
        columnToSort,
        disableUploads,
        documentsUploading,
        documentTypes,
        downloadDocument,
        downloadView,
        entityKey,
        errors,
        formDisabled,
        handleDocumentSort,
        handleRejectedDocs,
        includeSource,
        loading,
        noHeader,
        parentType,
        removeDocument,
        resetRequiredMissingDocuments,
        requiredDocs,
        role,
        savedDocuments,
        setAdminDocumentTypeListAvailable,
        sortDirection,
        stateCode,
        terminationDocument,
        toggleTerminationDoc,
        type,
        updateDocument, // (key, mediaAssetType)
        usedDocTypes,
        viewDocument,
        viewOnly,
    } = props;

    const [anchorEl, setAnchorEl] = useState(null);
    const [docKey, setDocKey] = useState(null);
    const [adminListReady, setAdminListReady] = useState(false);
    const [adminListRendered, setAdminListRendered] = useState(false);
    const [missingDocs, setMissingDocs] = useState([]);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);

    const prevProps = useRef({ savedDocuments, requiredDocs });

    useEffect(() => {
        adminDocumentTypeListAvailable();
        return () => {
            resetRequiredMissingDocuments();
        };
    }, []);

    useEffect(() => {
        if (
            !isEqual(savedDocuments, prevProps.current.savedDocuments) ||
            !isEqual(requiredDocs, prevProps.current.requiredDocs)
        ) {
            determineMissingRequiredDocuments();
        }
        prevProps.current = { savedDocuments, requiredDocs };
    }, [savedDocuments, requiredDocs]);

    const handleOpenDocumentMenu = (x, anchorElArg) => {
        setAnchorEl(anchorElArg);
        setDocKey(x);
    };

    const toggleDeleteDialog = (open, docKey) => {
        if (open) {
            handleMenuClose();
            setDeleteDialog(true);
            setDocToDelete(docKey);
        } else {
            setDeleteDialog(false);
            setDocToDelete(null);
        }
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const deleteAndMenuClose = () => {
        setAnchorEl(null);
        removeDocument(docToDelete);
        toggleDeleteDialog(false);
    };

    const previewAndMenuClose = (docKey, url) => {
        setAnchorEl(null);
        viewDocument(docKey, url);
    };

    const handleAddDocuments = (accepted, rejected, ownerType) => {
        addDocuments(entityKey, type, accepted, ownerType);
        if (rejected.length > 0) {
            handleRejectedDocs(rejected);
        }
    };

    const handleApproveDocumentAndClose = (docKey) => {
        setAnchorEl(null);
        approveDocument(docKey);
    };

    const adminDocumentTypeListAvailable = () => {
        if (parentType && !adminListRendered) {
            setAdminListRendered(true);
            setAdminDocumentTypeListAvailable();
            setAdminListReady(true);
        }
    };

    const determineMissingRequiredDocuments = () => {
        const docTypeRequiredList = get(props, "requiredDocs", []);

        const fulfilledDocs = docTypeRequiredList
            .map((requiredDocument) => {
                const savedDocumentMatch = find(savedDocuments, (savedDoc) => {
                    return requiredDocument.documentTypeKey === get(savedDoc, "documentType.documentTypeKey");
                });

                return savedDocumentMatch ? get(savedDocumentMatch, "documentType.documentTypeKey") : null;
            })
            .filter((doc) => doc);

        const missingDocsLocal = docTypeRequiredList.filter(
            (document) => !fulfilledDocs.includes(document.documentTypeKey)
        );
        setMissingDocs(missingDocsLocal);
    };

    const renderHelperMessage = (type) => {
        return (
            <React.Fragment>
                {type === "comp" && (
                    <div className="helperText">For best results, the Marketing Flyer Photo should be 1224x440px.</div>
                )}
                {type === "voucher" && (
                    <div className="helperText">
                        You will be paid fastest if you attach your Commission Payment or Equivalent
                    </div>
                )}
                {type === "property" && (
                    <div className="helperText">
                        Preferred specs for Primary Photos:
                        <div className="helperTextFlexBox">
                            <div>4:3 Aspect Ratio</div>
                            <div>1440 x 1080 Pixels (Resolution)</div>
                        </div>
                    </div>
                )}
                {missingDocs.length > 0 && (
                    <div className="helperText missingDocumentsText">
                        <h4>Missing required documents for submission:</h4>
                        <div className="helperTextFlexBox">
                            {missingDocs.map((doc) => (
                                <div>{doc["name"]}</div>
                            ))}
                        </div>
                    </div>
                )}
            </React.Fragment>
        );
    };

    const open = Boolean(anchorEl);

    return viewOnly ? (
        <DocumentListViewOnly
            {...props}
            open={open}
            anchorEl={anchorEl}
            docMenuKey={docKey}
            documentTypes={documentTypes}
            handlePreviewDocument={previewAndMenuClose}
            handleDownloadDocument={downloadDocument}
            handleMenuClose={handleMenuClose}
            handleOpenDocumentMenu={handleOpenDocumentMenu}
            downloadView={downloadView === true}
            includeSource={includeSource}
            type={type}
        />
    ) : (
        <DocumentDropContainer>
            {loading ? (
                <CircularProgress size={20} />
            ) : // No documents are attached
            Object.keys(savedDocuments).length === 0 && Object.keys(documentsUploading).length === 0 ? (
                <React.Fragment>
                    <DropzoneStyled>
                        <Dropzone
                            accept={acceptedFileTypes}
                            disabled={disableUploads}
                            onDrop={(accepted, rejected) => {
                                handleAddDocuments(accepted, rejected, type);
                            }}
                        >
                            {({ getRootProps, getInputProps }) => (
                                <DropzoneSmallTextWrapper {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <IconSmall />
                                    <div className="dropzoneTextContainer" {...getRootProps()}>
                                        <input {...getInputProps()} />
                                        <div className="dropzoneUploadHeaderText">
                                            Drop files on page to attach or browse here
                                        </div>
                                        <div className="dropzoneUploadText">
                                            (.csv, .doc, .docx, .jpg, .jpeg, .msg, .pdf, .png, .xls, and .xlsx files
                                            only)
                                        </div>
                                    </div>
                                </DropzoneSmallTextWrapper>
                            )}
                        </Dropzone>
                    </DropzoneStyled>

                    {renderHelperMessage(type)}
                </React.Fragment>
            ) : (
                // Documents are attached
                <React.Fragment>
                    <DocumentListRefactor
                        {...props}
                        handleAddDocuments={handleAddDocuments}
                        errors={errors}
                        header={
                            type === "user"
                                ? standardHeaders
                                : role === "portlandBrokerAdmin"
                                ? approvalHeaders
                                : standardHeaders
                        }
                        handleSort={handleDocumentSort}
                        columnToSort={columnToSort}
                        sortDirection={sortDirection}
                        selectDocumentType={updateDocument}
                        allDocumentTypes={allDocumentTypes}
                        documentTypes={documentTypes}
                        usedDocTypes={usedDocTypes}
                        handleOpenDocumentMenu={handleOpenDocumentMenu}
                        handleMenuClose={handleMenuClose}
                        toggleDeleteDialog={toggleDeleteDialog}
                        handlePreviewDocument={previewAndMenuClose}
                        anchorEl={anchorEl}
                        open={open}
                        docMenuKey={docKey}
                        savedDocuments={savedDocuments}
                        documentsUploading={documentsUploading}
                        formDisabled={formDisabled}
                        role={role}
                        approveDocument={handleApproveDocumentAndClose}
                        docApproved={Boolean(get(savedDocuments, docKey + ".approvedBy", null) != null)}
                        toggleTerminationDoc={toggleTerminationDoc}
                        terminationDocument={terminationDocument}
                        parentEntityType={parentType}
                        stateCode={stateCode}
                        adminListReady={adminListReady}
                        noHeader={noHeader}
                    />

                    <DropzoneStyled>
                        <Dropzone
                            accept={acceptedFileTypes}
                            disabled={disableUploads}
                            onDrop={(accepted, rejected) => {
                                handleAddDocuments(accepted, rejected, type);
                            }}
                        >
                            {({ getRootProps, getInputProps }) => (
                                <DropzoneSmallTextWrapper {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <IconSmall />
                                    <div className="dropzoneTextContainer">
                                        <div className="dropzoneUploadHeaderText">
                                            Drop files on page to attach or browse here
                                        </div>
                                        <div className="dropzoneUploadText">
                                            (.csv, .doc, .docx, .jpg, .jpeg, .msg, .pdf, .png, .xls, and .xlsx files
                                            only)
                                        </div>
                                    </div>
                                </DropzoneSmallTextWrapper>
                            )}
                        </Dropzone>
                    </DropzoneStyled>

                    {renderHelperMessage(type)}
                </React.Fragment>
            )}
            <Dialog open={deleteDialog} maxWidth="sm" aria-labelledby="confirmation-dialog-title">
                <DialogTitle style={{ textAlign: "center" }} id="confirmation-dialog-title">
                    Delete Document?
                </DialogTitle>
                <DialogContent style={{ textAlign: "center" }}>
                    <div>Are you sure you want to delete this document? </div>
                    <div>This change cannot be undone </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => toggleDeleteDialog(false, docToDelete)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={() => deleteAndMenuClose()} color="primary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </DocumentDropContainer>
    );
};

export default DocumentDrop;
